import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { 
  Box, 
  CssBaseline,
  ThemeProvider,
  createTheme,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from './config/api';
import Login from './pages/Login';
import AdminDashboard from './pages/admin/Dashboard';
import OwnerDashboard from './pages/owner/Dashboard';
import BusinessReports from './pages/owner/BusinessReports';
import AddBusiness from './pages/owner/AddBusiness';
import Sales from './pages/owner/Sales';
import AuditLogs from './pages/owner/AuditLogs';
import BackendLogs from './pages/owner/BackendLogs';
import CreditManagement from './pages/owner/CreditManagement';
import BusinessManagement from './pages/owner/BusinessManagement';
import BusinessSettings from './pages/owner/BusinessSettings';
import CreateUser from './pages/admin/CreateUser';
import UserManagement from './pages/admin/UserManagement';
import EditUser from './pages/admin/EditUser';
import TeamManagement from './pages/admin/TeamManagement';
import JobTagManagement from './pages/admin/JobTagManagement';
import Checks from './pages/admin/Checks';
import LocationManagement from './pages/admin/LocationManagement';
import Reports from './pages/admin/Reports';
import AssignChecks from './pages/admin/AssignChecks';
import PushNotifications from './pages/admin/PushNotifications';
import IncidentManagement from './pages/admin/IncidentManagement';
import IssueManagement from './pages/admin/IssueManagement';
import AssetManagement from './pages/admin/AssetManagement';
import AssetViewPage from './pages/admin/AssetViewPage';
import LocationViewPage from './pages/admin/LocationViewPage';
import MotTax from './pages/admin/MotTax';
import VehicleEvaluation from './pages/admin/VehicleEvaluation';
import SupportTickets from './pages/admin/SupportTickets';
import AcceptInvitation from './pages/AcceptInvitation';
import SubmitLicense from './pages/public/SubmitLicense';
import PublicCompleteCheck from './pages/public/PublicCompleteCheck';
import DriverCheck from './pages/public/DriverCheck';
import ResetPassword from './pages/ResetPassword';
import DownloadPage from './pages/download/DownloadPage';
import AssetRedirect from './pages/AssetRedirect';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import Support from './pages/Support';
import Settings from './pages/admin/Settings';
import DVLA from './pages/admin/DVLA';
import DVLACreditUsage from './pages/admin/DVLACreditUsage';
import HR from './pages/admin/HR';
import AdminLayout from './layouts/AdminLayout';
import OwnerLayout from './layouts/OwnerLayout';
import MultiBusinessLayout from './layouts/MultiBusinessLayout';
import Profile from './pages/admin/Profile';
import UserProfile from './pages/admin/UserProfile';
import Notifications from './pages/admin/Notifications';
import SettingsPage from './pages/owner/Settings';
import CreateSuperAdmin from './pages/owner/CreateSuperAdmin';
import GlobalTemplateManagement from './pages/owner/GlobalTemplateManagement';
import GlobalAssetTypeManagement from './pages/owner/GlobalAssetTypeManagement';
import SupportTicketManagement from './pages/owner/SupportTicketManagement';
import PrivateRoute from './components/PrivateRoute';
import LoanChecks from './pages/admin/LoanChecks';
import DeliveryChecks from './pages/admin/DeliveryChecks';
import LocationChecks from './pages/admin/LocationChecks';
import Templates from './pages/admin/Templates';
import Installations from './pages/admin/Installations';
import MultiBusinessDashboard from './pages/multi-business/Dashboard';
import TransferHistory from './pages/multi-business/TransferHistory';
import MultiBusinessManagement from './pages/owner/MultiBusinessManagement';
import BusinessTransferGroups from './pages/owner/BusinessTransferGroups';
import DVLAAuditLogs from './pages/owner/DVLAAuditLogs';
import Documentation from './pages/owner/Documentation';
import BusinessUiLabels from './pages/admin/BusinessUiLabels';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import Communications from './pages/admin/Communications';
import CalendarPage from './pages/admin/calendar/CalendarPage';
import CalendarSettingsPage from './pages/admin/calendar/CalendarSettingsPage';
import BookingsListPage from './pages/admin/calendar/BookingsListPage';
import PublicBookingPage from './pages/public/PublicBookingPage';
import PublicBookingConfirmationPage from './pages/public/PublicBookingConfirmationPage';

// Create a modern theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#2563eb',
      light: '#60a5fa',
      dark: '#1e40af',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#7c3aed',
      light: '#a78bfa',
      dark: '#5b21b6',
      contrastText: '#ffffff',
    },
    success: {
      main: '#059669',
      light: '#34d399',
      dark: '#047857',
    },
    warning: {
      main: '#d97706',
      light: '#fbbf24',
      dark: '#b45309',
    },
    error: {
      main: '#dc2626',
      light: '#f87171',
      dark: '#b91c1c',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Lato", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: 'Roboto',
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: 'Roboto',
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: 'Roboto',
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: 'Roboto',
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: 'Roboto',
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: 'Roboto',
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontFamily: 'Lato',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: 'Lato',
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    button: {
      fontFamily: 'Lato',
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: ({ theme }) => ({
          '&.MuiInputLabel-shrink': {
            paddingLeft: 4,
            paddingRight: 4,
            marginLeft: -4,
            backgroundColor: theme.palette.background.paper,
          },
        }),
      },
    },
  },
});

type AppUserRole = 'Admin' | 'Owner' | 'Super Admin' | 'Team Leader' | 'Manager' | 'User' | 'Multi Business Manager';

interface User {
  role: AppUserRole;
  token: string;
}

const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Keep default Authorization in sync so child routes never send stale/missing tokens before effects run
  const syncAxiosAuthFromStorage = () => {
    const t = localStorage.getItem('token');
    if (t) {
      axios.defaults.headers.common.Authorization = `Bearer ${t}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  };
  syncAxiosAuthFromStorage();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const decodedToken = decodeJWT(token);
        if (!decodedToken || !decodedToken.role) {
          throw new Error('Invalid token');
        }

        const userData = {
          role: decodedToken.role as AppUserRole,
          token: token
        };

        // Set the user data first to prevent immediate logout
        setUser(userData);
        setUserRole(userData.role);
        setAuthenticated(true);

        // Then verify token with backend
        try {
          await axios.get(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error('Token verification failed:', error);
          // Don't immediately log out, let the individual components handle auth errors
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        setUser(null);
        setUserRole(null);
        setAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Add axios interceptor for authentication
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // Add response interceptor to handle 401 errors
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          // Only redirect to login if we're not already on the login page
          if (window.location.pathname !== '/') {
            // Show error message first
            console.error('Session expired. Please log in again.');
            // Add a delay before redirecting
            setTimeout(() => {
              localStorage.removeItem('token');
              window.location.href = '/';
            }, 2000);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const handleLogin = (userData: { role: AppUserRole; token: string, businessName?: string }) => {
    localStorage.setItem('token', userData.token);
    syncAxiosAuthFromStorage();

    // Store business name if available
    if (userData.businessName) {
      localStorage.setItem('businessName', userData.businessName);
    }

    setUser(userData);
    setUserRole(userData.role);
    setAuthenticated(true);
    
    // Redirect based on role
    let redirectPath = '/admin/dashboard'; // Default for regular users
    
    if (userData.role === 'Owner' || userData.role === 'Super Admin') {
      redirectPath = '/owner';
    } else if (userData.role === 'Multi Business Manager') {
      redirectPath = '/multi-business';
    }
    
    window.location.href = redirectPath;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  console.log('Current user:', user);

  // Check if maintenance mode is enabled via environment variable (live launch overlay; set REACT_APP_MAINTENANCE_MODE=true to re-enable)
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {isMaintenanceMode && <MaintenanceOverlay />}
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              !authenticated ? (
                <Login onLogin={handleLogin} />
              ) : (
                // Update initial redirect logic
                <Navigate to={
                  (user?.role === 'Owner' || user?.role === 'Super Admin') ? '/owner' :
                  user?.role === 'Multi Business Manager' ? '/multi-business' :
                  '/admin/dashboard'
                } replace />
              )
            }
          />
          <Route
            path="/login"
            element={
              !authenticated ? (
                <Login onLogin={handleLogin} />
              ) : (
                 // Update login redirect logic
                <Navigate to={
                  (user?.role === 'Owner' || user?.role === 'Super Admin') ? '/owner' :
                  user?.role === 'Multi Business Manager' ? '/multi-business' :
                  '/admin/dashboard'
                } replace />
              )
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/asset/:serial" element={<AssetRedirect />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/support" element={<Support />} />
          <Route path="/submit-license/:token" element={<SubmitLicense />} />
          <Route path="/complete-check/:token" element={<PublicCompleteCheck />} />
          <Route path="/driver/:token" element={<DriverCheck />} />
          {/* Public per-business booking page: app.check-now.co.uk/:slug/booking */}
          <Route path="/:businessSlug/booking" element={<PublicBookingPage />} />
          <Route path="/:businessSlug/booking/confirmed/:token" element={<PublicBookingConfirmationPage />} />
          
          {/* Multi-Business Manager Route */}
          <Route
            path="/multi-business/*"
            element={
              user?.role === 'Multi Business Manager' ? (
                <PrivateRoute 
                  isAuthenticated={authenticated} 
                  allowedRoles={['Multi Business Manager']}
                  userRole={userRole}
                >
                  <MultiBusinessLayout />
                </PrivateRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          >
            <Route index element={<MultiBusinessDashboard />} />
            <Route path="dashboard" element={<MultiBusinessDashboard />} />
            <Route path="transfer" element={<MultiBusinessDashboard />} />
            <Route path="transfer-history" element={<TransferHistory />} />
            <Route path="*" element={<Navigate to="/multi-business" replace />} />
          </Route>
          
          <Route
            path="/admin/*"
            element={
              // Update to check for impersonation or allowed admin roles
              (user?.role === 'Admin' || user?.role === 'Team Leader' || user?.role === 'Manager' || user?.role === 'User' || localStorage.getItem('impersonatingBusiness') === 'true' || localStorage.getItem('inBusinessContext') === 'true') ? (
                <PrivateRoute 
                  isAuthenticated={authenticated} 
                  allowedRoles={['Admin', 'Manager', 'User', 'Team Leader']}
                  userRole={userRole}
                >
                  <AdminLayout />
                </PrivateRoute>
              ) : (
                <Navigate to="/" replace /> // Redirect others to login/home
              )
            }
          >
            {/* Change index route to redirect to dashboard */}
            <Route index element={<Navigate to="/admin/dashboard" replace />} /> 
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="settings" element={<Settings />} />
            <Route path="safety-checks" element={<Checks />} />
            <Route path="templates" element={<Templates />} />
            <Route path="loan-checks" element={<LoanChecks />} />
            <Route path="sync-conflicts" element={<Settings initialTab={6} />} />
            <Route path="delivery-checks" element={<DeliveryChecks />} />
            <Route path="location-checks" element={<LocationChecks />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="calendar/bookings" element={<BookingsListPage />} />
            <Route path="calendar/settings" element={<CalendarSettingsPage />} />
            <Route path="installations" element={<Installations />} />
            <Route path="incidents" element={<IncidentManagement />} />
            <Route path="issues" element={<IssueManagement />} />
            <Route path="assets" element={<AssetManagement />} />
            <Route path="asset-views/:viewId" element={<AssetViewPage />} />
            <Route path="location-views/:viewId" element={<LocationViewPage />} />
            <Route path="mot-tax" element={<MotTax />} />
            <Route path="vehicle-evaluation" element={<VehicleEvaluation />} />
            <Route path="profile" element={<Profile />} />
            <Route path="user-profile" element={<UserProfile />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="users/edit/:userId" element={<EditUser />} />
            <Route path="create-user" element={<CreateUser />} />
            <Route path="teams" element={<TeamManagement />} />
            <Route path="locations" element={<LocationManagement />} />
            <Route path="assign-checks" element={<AssignChecks />} />
            <Route path="job-tags" element={<JobTagManagement />} />
            <Route path="reports" element={<Reports />} />
            <Route path="communications" element={<Communications />} />
            <Route path="push-notifications" element={<PushNotifications />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="support-tickets" element={<SupportTickets />} />
            <Route path="dvla" element={<DVLA />} />
            <Route path="dvla-credit-usage" element={<DVLACreditUsage />} />
            <Route path="hr" element={<HR />} />
            <Route path="documentation" element={<Documentation />} />
            <Route path="business-ui-labels" element={<BusinessUiLabels />} />
          </Route>
          <Route
            path="/owner/*"
            element={
              // Allow 'Owner' and 'Super Admin' access to /owner/* routes
              (user?.role === 'Owner' || user?.role === 'Super Admin') ? (
                <PrivateRoute 
                  isAuthenticated={authenticated} 
                  allowedRoles={['Owner', 'Super Admin']}
                  userRole={userRole}
                >
                  <OwnerLayout />
                </PrivateRoute>
              ) : (
                <Navigate to="/" replace /> // Redirect others (Admin) to login/home
              )
            }
          >
            <Route path="dashboard" element={<OwnerDashboard />} />
            <Route path="reports" element={<BusinessReports />} />
            <Route path="add-business" element={<AddBusiness />} />
            <Route path="business-management" element={<BusinessManagement />} />
            <Route path="business-transfer-groups" element={<BusinessTransferGroups />} />
            <Route path="multi-business-management" element={<MultiBusinessManagement />} />
            <Route path="business-settings/:businessId" element={<BusinessSettings />} />
            <Route path="global-templates" element={<GlobalTemplateManagement />} />
            <Route path="global-asset-types" element={<GlobalAssetTypeManagement />} />
            <Route path="support-tickets" element={<SupportTicketManagement />} />
            <Route path="sales" element={<Sales />} />
            <Route path="audit-logs" element={<AuditLogs />} />
            <Route path="dvla-audit-logs" element={<DVLAAuditLogs />} />
            <Route path="backend-logs" element={<BackendLogs />} />
            <Route path="credit-management" element={<CreditManagement />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="create-super-admin" element={<CreateSuperAdmin />} />
            <Route path="documentation" element={<Documentation />} />
            <Route index element={<Navigate to="/owner/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/owner/dashboard" replace />} />
          </Route>
          <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App; 